import { useCallback, useEffect, useState } from 'react';
import { enumerate } from '../lib/telephony/devices';

export interface AudioDevicesState {
  microphones: MediaDeviceInfo[];
  speakers: MediaDeviceInfo[];
  supported: boolean;
  refresh: () => void;
}

/**
 * Live list of audio input/output devices. Re-enumerates on `devicechange`.
 * Returns empty arrays when the browser has no MediaDevices API.
 */
export function useAudioDevices(): AudioDevicesState {
  const [state, setState] = useState<{
    microphones: MediaDeviceInfo[];
    speakers: MediaDeviceInfo[];
  }>({ microphones: [], speakers: [] });

  const supported = typeof navigator !== 'undefined' && !!navigator.mediaDevices;

  const refresh = useCallback(async () => {
    if (!supported) return;
    try {
      const devices = await enumerate();
      setState(devices);
    } catch (err) {
      console.warn('[useAudioDevices] enumerate failed', err);
    }
  }, [supported]);

  useEffect(() => {
    if (!supported) return;
    void refresh();
    const onChange = () => void refresh();
    navigator.mediaDevices.addEventListener('devicechange', onChange);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', onChange);
    };
  }, [refresh, supported]);

  return { ...state, supported, refresh };
}
